import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiSearchParameters } from './interfaces/ApiSearchParameters';
import { IExportParameters } from './interfaces/IExportParameters';
import { IApiExportRequest } from './interfaces/IApiExportResponse';
import { ApiResourceService } from 'libraries/service/apiResource/apiResource.service';
import { IBookingSearchParameters } from 'libraries/interfaces/IBookingSearchParameters';
import { IApiBookingSearchResponse } from 'libraries/service/resources/booking/IApiBookingSearchResponse';
import { IAirBooking } from 'libraries/service/resources/model/booking/air/IAirBooking';
import {
    IGetBookingsStayResponse,
    IPrice,
    IPriceDetail,
    IProperty,
    ITraveller,
    TGetBookingResponse,
    IGetBookingOptions,
    IGetBookingsAirResponse,
    IBookingPropertyDescription,
    IRule,
    IRulePenalty,
    IGetVehicleRentalBookingOptions, IOriginalCurrency
} from './interfaces/IGetBookings';
import { pluck, tap } from 'rxjs/operators';
import { ExportFormats, IApiExportResponse } from 'libraries/interfaces/IApiExportResponse';
import { BookingType } from './interfaces/get-bookings.enum';
import { ITaskResponse } from "libraries/service/resources/booking-task-resource/ITask";
import { INotepad } from "libraries/service/resources/booking/interfaces/notepad.interface";
import {
    IBookingHostCode,
    IImportBookingResponse
} from "libraries/service/resources/booking/interfaces/import.interface";
import { ICancelBookingResponse } from './interfaces/ICancelBooking';
import { IDeleteSectorsRequest } from "../model/booking/air/IAirCreateBooking";
import { IWenji } from "libraries/interfaces/IWenji";
import { UrlHelperService } from "libraries/service/url-helper/url-helper.service";
declare const wenji: IWenji;

/**
 * Response from the API. Contains multiple checks results.
 */
export interface IQualityCheckResponse extends ITaskResponse {
    readonly service: IQualityCheckResult[];
}

/**
 * Resut of a single check (aka service).
 */
export interface IQualityCheckResult {
    readonly code: string;
    readonly match: boolean;
    readonly label: string;
    readonly message: string[];
}

const BOOKING: string = 'booking';
const BOOKINGS: string = 'bookings';
const VEHICLE_RENTAL: string = 'vehicleRental';
const VOUCHER: string = 'voucher';
const GENERATE: string = 'generate';
const GET: string = 'get';
const SEARCH: string = 'search';
const EXPORT: string = 'export';
const XSLX: string = 'xslx';
const CANCEL: string = 'cancel';
const VOID: string = 'void';
const NOTE: string = 'note';
const APIS: string = 'apis';
const SYNCHRONIZE: string = 'synchronize';
const HOTEL: string = 'hotel';

@Injectable()
export class BookingResourceService {
    constructor(
        private readonly apiResource: ApiResourceService,
        private readonly urlHelperService: UrlHelperService,
    ) {
    }

    public getBookingsBySyscode(
        syscode: string[],
        computeCancellable: boolean = false,
        computeIssuable: boolean = false,
        computeVoidable: boolean = false): Observable<Array<IGetBookingsAirResponse | IGetBookingsStayResponse>> {
        return this.apiResource.post(
            this.apiResource.getApiUrl(BOOKING, BOOKINGS, GET),
            {syscode},
            {computeCancellable, computeIssuable, computeVoidable}
        );
    }

    public getBookingsBySyscodeWithFilter(
        syscode: string[],
        fields: string[],
        computeCancellable: boolean = false,
        computeIssuable: boolean = false,
        computeVoidable: boolean = false,
        filterFieldsWithConfig: boolean = false,): Observable<Array<IGetBookingsAirResponse | IGetBookingsStayResponse>> {
        return this.apiResource.post(
            this.apiResource.getApiUrl(BOOKING, BOOKINGS, GET),
            {syscode, fields},
            {computeCancellable, computeIssuable, computeVoidable, filterFieldsWithConfig}
        );
    }

    public getBooking(syscode: string, options?: IGetBookingOptions): Observable<TGetBookingResponse> {
        return this.apiResource.getOne<TGetBookingResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                this.urlHelperService.retrieveCharacters(syscode)
            ),
            options,
        ).pipe(
            tap(this.sanitizeBooking.bind(this))
        );
    }

    public getVehicleRentalBooking(syscode: string, options?: IGetVehicleRentalBookingOptions): Observable<TGetBookingResponse> {
        return this.apiResource.getOne<TGetBookingResponse>(
            this.apiResource.getApiUrl(
                VEHICLE_RENTAL,
                BOOKING,
                this.urlHelperService.retrieveCharacters(syscode)
            ),
            options,
        ).pipe(
            tap(this.sanitizeBooking.bind(this))
        );
    }

    public search(parameters: IBookingSearchParameters): Observable<IApiBookingSearchResponse> {
        return this.apiResource.postOne<ApiSearchParameters, IApiBookingSearchResponse>(
            this.apiResource.getApiUrl(BOOKING, BOOKINGS, SEARCH),
            ApiSearchParameters.fromSearchParameters(parameters)
        );
    }

    public searchExport(parameters: IBookingSearchParameters, isCarbon: boolean): Observable<IApiExportResponse> {
        return this.apiResource.postOne<ApiSearchParameters, IApiExportResponse>(
            this.apiResource.getApiUrl(
                BOOKING, BOOKINGS, SEARCH, EXPORT, XSLX
            ),
            ApiSearchParameters.fromSearchParameters(parameters, isCarbon)
        );
    }

    public export(parameters: IExportParameters): Observable<IApiExportResponse> {
        return this.apiResource.postOne<IApiExportRequest, IApiExportResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                EXPORT,
                parameters.format),
            {
                syscode: parameters.syscodes,
                format: parameters.format,
            }
        );
    }

    public issue(syscode: string): Observable<IAirBooking> {
        return this.postToSimpleBookingEndpoint(syscode, "issue");
    }

    public cancel(syscode: string): Observable<ICancelBookingResponse> {
        return this.apiResource.postOne<{}, ICancelBookingResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                this.urlHelperService.retrieveCharacters(syscode),
                CANCEL
            ),
            {}
        );
    }

    public void(syscode: string): Observable<TGetBookingResponse> {
        return this.apiResource.postOne<{}, TGetBookingResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                syscode,
                VOID
            ),
            {}
        ).pipe(
            tap(this.sanitizeBooking.bind(this))
        );
    }

    public updateNotes(syscode: string, notes: INotepad[]): Observable<TGetBookingResponse> {
        return this.apiResource.putOne<{}, TGetBookingResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                syscode,
                NOTE,
            ),
            {note: notes}
        );
    }

    public updateTravellerIdentity(syscode: string, traveller: ITraveller[]): Observable<TGetBookingResponse> {
        return this.apiResource.putOne<{}, TGetBookingResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                syscode,
                APIS,
            ),
            {traveller}
        );
    }

    public importBooking(bookingHostCode: IBookingHostCode[]): Observable<IImportBookingResponse> {
        return this.apiResource.postOne<{}, IImportBookingResponse>(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                SYNCHRONIZE,
            ),
            {
                booking: bookingHostCode
            }
        );
    }

    public getBookingHtmlVoucherAsBlob(sysCode: string): Observable<Blob> {
        return this.apiResource.getBlob(
            this.apiResource.getApiUrl(
                HOTEL,
                BOOKINGS,
                sysCode,
                VOUCHER,
            ),
            {format: ExportFormats.HTML}
        );
    }

    public getVehicleVoucher(vehicleData: any): Observable<any> {
        return this.apiResource.postAndGetHtml<any>(
            this.apiResource.getApiUrl(
                VEHICLE_RENTAL,
                VOUCHER,
                GENERATE,
            ),
            vehicleData
        );
    }

    public deleteBookingSectors(syscode: string, endpoints: string, body: IDeleteSectorsRequest): Observable<any> {
        return this.apiResource.deleteWithBody(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                syscode,
                endpoints
            ),
            body,
            {
                format: 'merged'
            },
            {
                target: wenji.ui.target
            }
        );
    }

    private sanitizeBooking(booking: TGetBookingResponse): void {
        // For compatibility reasons, we can have bookings with properties that are not arrays
        // as we cannot synchronise hotel bookings for the moment

        if (booking.type && booking.type.some((bookingType: BookingType):boolean => bookingType === BookingType.stay)) {
            const property: IProperty[] | IProperty = (booking as IGetBookingsStayResponse).property;
            if (property && !Array.isArray(property)) {
                (booking as IGetBookingsStayResponse).property = [property];
            }
            (booking as IGetBookingsStayResponse).price = parseStayPrice((booking as IGetBookingsStayResponse).price);
            (booking as IGetBookingsStayResponse).property = parseStayProperty((booking as IGetBookingsStayResponse).property);
            (booking as IGetBookingsStayResponse).rule = parseRuleProperty((booking as IGetBookingsStayResponse).rule);
        }
    }

    private postToSimpleBookingEndpoint(syscode: string, endpoints: string, body: object = {}): Observable<IAirBooking> {
        return this.apiResource.postOne(
            this.apiResource.getApiUrl(
                BOOKING,
                BOOKINGS,
                syscode,
                endpoints
            ),
            body
        ).pipe(pluck('booking'));
    }
}

function parseRuleProperty(ruleProperty: IRule[]): IRule[] {
    return (ruleProperty || []).map(
        (property: IRule): IRule => {
            if (property && property.penalty) {
                const penalty: IRulePenalty[] = property.penalty
                    .map((penaltyItem: IRulePenalty): IRulePenalty => {
                        // @ts-ignore
                        if (penaltyItem.validity && penaltyItem.validity.validity) {
                            // @ts-ignore
                            penaltyItem.validity.cancellation = penaltyItem.validity.validity;
                            // @ts-ignore
                            delete penaltyItem.validity.validity;
                        }
                        return penaltyItem;
                    });
                return {
                    ...property,
                    penalty,
                };
            }
            return property;
        }
    );
}

function parseStayProperty(bookingProperty: IProperty[]): IProperty[] {
    return (bookingProperty || []).map(
        (property: IProperty): IProperty => {
            let description: IBookingPropertyDescription[] = property.description;
            if (description) {
                description = (Array.isArray(description) ? description : [description])
                    .map(
                        (descriptionItem: IBookingPropertyDescription): IBookingPropertyDescription => {
                            return descriptionItem;
                        }
                    );
            }
            return {
                ...property,
                description
            };
        }
    );
}

function parseStayPrice(bookingPrice: IPrice[]): IPrice[] {
    return (bookingPrice || []).map(
        (priceItem: IPrice): IPrice => {
            if (!priceItem.detail || !(Object.keys(priceItem.detail).length > 0) || !priceItem.detail.base) {
                return priceItem;
            }
            const priceDetailBase: IPriceDetail[] = priceItem.detail.base.map(
                (basePrice: IPriceDetail): IPriceDetail => ({
                    ...basePrice,
                    accomodation: basePrice.accomodation && !Array.isArray(basePrice.accomodation) ?
                        [basePrice.accomodation] : basePrice.accomodation,
                    traveller: basePrice.traveller && !Array.isArray(basePrice.traveller) ?
                        [basePrice.traveller] : basePrice.traveller,
                })
            );
            // @ts-ignore
            if (priceItem.detail.supplement && priceItem.detail.supplement.amount) {
                // @ts-ignore
                priceItem.detail.supplement = [priceItem.detail.supplement];
                // @ts-ignore
            } else if (
                priceItem.detail.supplement &&
                !Array.isArray(priceItem.detail.supplement) &&
                // @ts-ignore
                priceItem.detail.supplement.originalCurrency &&
                // @ts-ignore
                !priceItem.detail.supplement.amount
            ) {
                // @ts-ignore
                const originalCurrency: IOriginalCurrency = priceItem.detail.supplement.originalCurrency;
                priceItem.detail.supplement = [
                    {
                        // @ts-ignore
                        ...priceItem.detail.supplement,
                        ...originalCurrency
                    }
                ];
            }
            // @ts-ignore
            const priceDetailSupplement: IPriceDetail[] = priceItem.detail.supplement ? priceItem.detail.supplement.map(
                (price: IPriceDetail): IPriceDetail => ({
                    ...price,
                    accomodation: price.accomodation && !Array.isArray(price.accomodation) ?
                        [price.accomodation] : price.accomodation,
                    traveller: price.traveller && !Array.isArray(price.traveller) ?
                        [price.traveller] : price.traveller,
                })
            ) : undefined;
            return {
                ...priceItem,
                detail: {
                    ...priceItem.detail,
                    base: priceDetailBase,
                    supplement: priceDetailSupplement,
                }
            };
        }
    );
}
